<template>
  <div class="impact-page">
    <impact-header title="请假 / 旷课影响" :tags="['老师请假 + 旷课', '3 小时线']"
                   :chips="chips" :loaded-at="loadedAt"></impact-header>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">核心指标</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
      </div>
      <div class="section-filters">
        <filter-pills v-model="days" :options="dayOptions" label="时间窗口" @on-change="load"></filter-pills>
        <span class="filter-note">※ 关键不是请假多少，而是来不及代课的有多少：3 小时外安排代课，3 小时内不安排代课</span>
      </div>
      <div class="kpi-grid">
        <Spin v-if="loading" fix></Spin>
        <kpi-card v-for="card in kpiCards" :key="card.key" v-bind="card">
          <div v-if="card.key === 'scope'" class="kpi-split">
            <div>
              <div class="kpi-split-value">{{ fmtNum(d.studentCount) }}<small>人</small></div>
              <div class="kpi-split-label">学生（去重）</div>
            </div>
            <div class="kpi-split-right">
              <div class="kpi-split-value is-warning">{{ fmtNum(d.lessonCount) }}<small>课节</small></div>
              <div class="kpi-split-label">受影响课节</div>
            </div>
          </div>
        </kpi-card>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">3 小时线</h2>
      </div>
      <p class="block-lead">3 小时是硬边界：3 小时外系统安排代课，3 小时内没有兜底。系统里家长取消与改期补回无法区分，结果只分「成功代课 / 未代课」。</p>
      <div class="grid g12">
        <div class="card">
          <div class="card-head">
            <span class="card-title">核心对照</span>
          </div>
          <div class="compare-boxes">
            <div class="compare-box">
              <div class="compare-box-title is-healthy">3 小时外请假</div>
              <div class="compare-box-value">{{ fmtRateNum(d.outerLossRate) }}<small>% 未代课率</small></div>
              <div class="compare-box-foot">{{ fmtNum(d.outerCount) }} 课节 · 代课率 {{ fmtRate(d.outerProxyRate) }}</div>
            </div>
            <div class="compare-box is-danger">
              <div class="compare-box-title is-danger">3 小时内请假 / 旷课</div>
              <div class="compare-box-value is-danger">{{ fmtRateNum(d.innerLossRate) }}<small>% 未代课率</small></div>
              <div class="compare-box-foot">{{ fmtNum(d.innerCount) }} 课节 · 占受影响 {{ fmtRate(d.innerShare) }}</div>
            </div>
          </div>
          <div class="note is-danger" v-if="num(d.lossCount) > 0">
            <b>3 小时内的课节只占 {{ fmtRate(d.innerShare) }}，却贡献了 {{ fmtRate(d.innerLossShare) }} 的未代课。</b>这部分不安排代课，没有系统兜底。
          </div>
          <div class="note is-warning">既然 3 小时内不做代课，<b>唯一可控的杠杆是改约</b>：把自助改约入口和改约话术做进家长端，比建应急代课池更直接。</div>
          <dl class="kv">
            <dt>3h 内调度策略</dt><dd>不安排代课</dd>
            <dt>3h 内未代课</dt><dd>{{ fmtNum(d.innerLossCount) }} 课节</dd>
            <dt>旷课未代课率</dt><dd>{{ fmtRate(d.absentLossRate) }}（占 {{ fmtRate(d.absentShare) }} 课节）</dd>
            <dt>请假 / 旷课事件</dt><dd>{{ fmtNum(d.eventCount) }} 次 · 次均 {{ fmtNum(d.avgLessonsPerEvent) }} 课节</dd>
          </dl>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">提前时长 × 处理结果<span class="card-sub">受影响课节 {{ fmtNum(d.lessonCount) }}</span></span>
            <span class="chart-legend">
              <span><i class="is-proxy"></i>成功代课</span>
              <span><i class="is-loss"></i>未代课</span>
            </span>
          </div>
          <bar-list :rows="bucketRows" unit="" :label-width="92" :right-width="130"></bar-list>
          <div class="bucket-axis" :style="{ paddingLeft: '102px', paddingRight: '140px' }">
            <span>◀ 3 小时外 · 系统安排代课</span>
            <span class="is-danger">3 小时线</span>
            <span>3 小时内 · 不安排代课 ▶</span>
          </div>
          <div class="note">提前时长 = 课节开始时间 − 老师最早一条请假申请时间。「无申请记录」= 状态为请假但查不到申请，只计入总量，不进 3 小时拆分。</div>
        </div>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">趋势与高频学生</h2>
      </div>
      <p class="block-lead">周趋势看整体是否恶化；高频被请假学生按风险排序，新生优先置顶。</p>
      <div class="grid g2">
        <div class="card">
          <div class="card-head">
            <span class="card-title">周趋势<span class="card-sub">截至今天近 10 个 ISO 周</span></span>
            <span class="chart-legend">
              <span><i class="is-lessons"></i>受影响课节</span>
              <span><i class="is-loss"></i>未代课</span>
            </span>
          </div>
          <div v-if="!weekly.length" class="chart-empty">暂无数据</div>
          <svg v-else class="svg-chart" :viewBox="'0 0 ' + trend.W + ' ' + trend.H">
            <g v-for="g in trend.grid" :key="'g' + g.y">
              <line class="gridline" :x1="trend.PL" :y1="g.y" :x2="trend.W - trend.PR" :y2="g.y"></line>
              <text class="axis-l" :x="trend.PL - 8" :y="g.y + 3" text-anchor="end">{{ g.label }}</text>
            </g>
            <text v-for="p in trend.lessons" :key="'x' + p.label" class="axis-l" :x="p.x" :y="trend.H - trend.PB + 16"
                  text-anchor="middle">{{ p.label }}</text>
            <path :d="trend.area" fill="#E89F2D" fill-opacity="0.08"></path>
            <path :d="trend.lessonPath" fill="none" stroke="#E89F2D" stroke-width="2"></path>
            <path :d="trend.lossPath" fill="none" stroke="#E6464E" stroke-width="2" stroke-dasharray="4 3"></path>
            <circle v-for="p in trend.lessons" :key="'a' + p.label" :cx="p.x" :cy="p.y" r="2.6" fill="#fff"
                    stroke="#E89F2D" stroke-width="1.6"><title>{{ p.title }}</title></circle>
            <circle v-for="p in trend.loss" :key="'b' + p.label" :cx="p.x" :cy="p.y" r="2.6" fill="#fff"
                    stroke="#E6464E" stroke-width="1.6"><title>{{ p.title }}</title></circle>
          </svg>
        </div>
        <div class="card">
          <div class="card-head">
            <span class="card-title">高频被请假学生<span class="card-sub">{{ studentSub }}</span></span>
          </div>
          <div class="table-wrap">
            <table class="impact-table">
              <thead>
              <tr>
                <th>学生</th>
                <th>新生状态</th>
                <th>主老师</th>
                <th class="num">被请假次数</th>
                <th class="num">3h内</th>
                <th class="num">影响课节</th>
                <th>老师请假日期</th>
                <th>风险</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in students" :key="row.uid" :class="{ 'is-priority': row.newPriority }">
                <td><span class="t-name">{{ row.userName || '—' }}</span><span class="t-id">{{ row.uid }}</span></td>
                <td>
                  <span v-if="row.newPriority" class="badge is-danger">新生优先</span>
                  <span v-else-if="row.isNew" class="badge is-warning">新生</span>
                  <span class="t-muted">{{ isNum(row.tenureDays) ? '开课第 ' + row.tenureDays + ' 天' : '—' }}</span>
                </td>
                <td>{{ row.tchName || '—' }}</td>
                <td class="num"><b>{{ fmtNum(row.leaveCount) }}</b></td>
                <td class="num" :class="{ 't-danger': num(row.innerCount) > 0 }">{{ fmtNum(row.innerCount) }}</td>
                <td class="num">{{ fmtNum(row.lessonCount) }}</td>
                <td class="wrap">
                  <span v-for="(dt, idx) in (row.dates || [])" :key="idx" class="mini-badge"
                        :class="dt.inner ? 'is-inner' : 'is-outer'">{{ shortDate(dt.date) }}</span>
                </td>
                <td><span class="badge" :class="levelBadge(row.level)">{{ row.level || '—' }}</span></td>
              </tr>
              <tr v-if="!students.length"><td colspan="8" class="empty">当前窗口内无高频被请假学生</td></tr>
              </tbody>
            </table>
          </div>
          <div class="note is-warning" v-if="studentInsight">{{ studentInsight }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ImpactHeader from './parts/ImpactHeader.vue'
import KpiCard from './parts/KpiCard.vue'
import BarList from './parts/BarList.vue'
import FilterPills from './parts/FilterPills.vue'
import {
  postImpact, num, isNum, fmtNum, fmtRate, fmtRateNum, shortDate, levelHigh, levelLow, DAY_OPTIONS, LEVEL_BADGE
} from './parts/impact'

const PROXY_COLOR = '#0B9E8D'
const LOSS_COLOR = '#E6464E'
const OTHER_COLOR = '#C9D0DC'

function niceMax (v) {
  if (v <= 10) {
    return 10
  }
  let step = Math.pow(10, Math.floor(Math.log10(v)))
  return Math.ceil(v / step) * step
}

export default {
  name: 'TchImpactLeave',
  components: { ImpactHeader, KpiCard, BarList, FilterPills },
  data () {
    return {
      loading: false,
      loadedAt: '',
      days: 90,
      dayOptions: DAY_OPTIONS,
      d: {}
    }
  },
  computed: {
    weekly () {
      return this.d.weekly || []
    },
    students () {
      return this.d.students || []
    },
    chips () {
      return [
        { label: '时间窗口', value: '近 ' + this.days + ' 天' },
        { label: '课节状态', value: '老师请假（状态）+ 考勤缺席' },
        { label: '3 小时线', value: '外 代课 / 内 不代课' },
        { label: '结果', value: '成功代课 / 未代课' }
      ]
    },
    kpiCards () {
      let d = this.d
      return [
        {
          key: 'scope',
          title: '受老师请假 / 旷课波及',
          level: num(d.lessonCount) > 0 ? 'warning' : 'healthy',
          period: '请假 / 旷课 ' + fmtNum(d.eventCount) + ' 次',
          compare: '次均 ' + fmtNum(d.avgLessonsPerEvent) + ' 课节',
          foot: '常规外教课：请假状态，或考勤标记老师缺席的课节'
        },
        {
          key: 'proxy',
          title: '代课安排率',
          value: fmtRateNum(d.proxyRate),
          unit: '%',
          level: levelHigh(d.proxyRate, 80, 60),
          period: '成功代课 ' + fmtNum(d.proxyCount) + ' 课节',
          compare: '3h 外 ' + fmtRate(d.outerProxyRate),
          compareTone: 'healthy',
          foot: '有代课课节（原课节派生）且已排或已完成 ÷ 受影响课节'
        },
        {
          key: 'loss',
          title: '未代课率',
          value: fmtRateNum(d.lossRate),
          unit: '%',
          level: levelLow(d.lossRate, 10, 25),
          period: fmtNum(d.lossCount) + ' 课节',
          compare: '3h 外 ' + fmtRate(d.outerLossRate) + ' vs 3h 内 ' + fmtRate(d.innerLossRate),
          compareTone: 'danger',
          foot: '未代课 = 损失课次，家长取消与改期补回在系统里无法区分'
        },
        {
          key: 'innerLoss',
          title: '3 小时内未代课',
          value: fmtNum(d.innerLossCount),
          unit: '课节',
          level: num(d.innerLossCount) > 0 ? 'danger' : 'healthy',
          period: '3h 内含 1–3h / <1h / 旷课',
          compare: '占全部未代课 ' + fmtRate(d.innerLossShare),
          compareTone: 'danger',
          foot: '3 小时内不安排代课，只能改约或取消'
        },
        {
          key: 'outer',
          title: '3 小时外请假',
          value: fmtNum(d.outerCount),
          unit: '课节',
          level: levelLow(d.outerLossRate, 5, 15),
          period: '未代课率 ' + fmtRate(d.outerLossRate),
          compare: '代课率 ' + fmtRate(d.outerProxyRate),
          compareTone: 'healthy',
          foot: '提前 > 3 小时申请，系统应安排代课'
        },
        {
          key: 'inner',
          title: '3 小时内请假 / 旷课',
          value: fmtNum(d.innerCount),
          unit: '课节',
          level: levelLow(d.innerShare, 15, 30),
          period: '占受影响 ' + fmtRate(d.innerShare),
          compare: '未代课率 ' + fmtRate(d.innerLossRate),
          compareTone: 'danger',
          foot: '没有系统兜底的课节'
        },
        {
          key: 'absent',
          title: '旷课',
          value: fmtNum(d.absentCount),
          unit: '课节',
          level: num(d.absentCount) > 0 ? 'danger' : 'healthy',
          period: '占受影响 ' + fmtRate(d.absentShare),
          compare: '未代课率 ' + fmtRate(d.absentLossRate),
          compareTone: 'danger',
          foot: '考勤标记老师缺席（is_tch_absent），归入 3 小时内'
        },
        {
          key: 'proxyCount',
          title: '成功代课',
          value: fmtNum(d.proxyCount),
          unit: '课节',
          level: 'healthy',
          period: '受影响 ' + fmtNum(d.lessonCount) + ' 课节',
          compare: '',
          foot: '代课课节状态为已约 / 待上 / 已完成'
        }
      ]
    },
    bucketRows () {
      return (this.d.buckets || []).map(function (b) {
        let other = Math.max(0, num(b.total) - num(b.proxy) - num(b.loss))
        let segs = [
          { value: b.proxy, color: PROXY_COLOR, title: '成功代课 ' + num(b.proxy) },
          { value: b.loss, color: LOSS_COLOR, title: '未代课 ' + num(b.loss) }
        ]
        if (other > 0) {
          segs.push({ value: other, color: OTHER_COLOR, title: '其他 ' + other })
        }
        let lossHigh = isNum(b.lossRate) && Number(b.lossRate) > 50
        return {
          key: b.key,
          label: b.label,
          labelClass: b.side === 'inner' ? 'is-danger' : (b.side === 'unknown' ? 'is-muted' : ''),
          segs: segs,
          value: b.total,
          extra: '未代课 ' + fmtRate(b.lossRate),
          extraClass: lossHigh ? 'is-danger' : ''
        }
      })
    },
    trend () {
      let W = 720
      let H = 240
      let PL = 40
      let PR = 16
      let PT = 16
      let PB = 30
      let list = this.weekly
      let n = list.length
      let maxV = 0
      list.forEach(function (w) {
        maxV = Math.max(maxV, num(w.lessons), num(w.loss))
      })
      let max = niceMax(maxV)
      let sx = function (i) {
        return n > 1 ? PL + i / (n - 1) * (W - PL - PR) : (PL + W - PR) / 2
      }
      let sy = function (v) {
        return H - PB - v / max * (H - PT - PB)
      }
      let grid = []
      for (let i = 0; i <= 4; i++) {
        grid.push({ y: H - PB - i / 4 * (H - PT - PB), label: Math.round(i / 4 * max) })
      }
      let pts = function (key, name) {
        return list.map(function (w, i) {
          return {
            label: w.label,
            x: sx(i),
            y: sy(num(w[key])),
            title: w.label + (w.weekStart ? '（' + w.weekStart + ' 起）' : '') + ' ' + name + ' ' + num(w[key])
          }
        })
      }
      let lessons = pts('lessons', '受影响课节')
      let loss = pts('loss', '未代课')
      let path = function (arr) {
        return arr.map(function (p, i) {
          return (i ? 'L' : 'M') + p.x + ',' + p.y
        }).join(' ')
      }
      let lessonPath = path(lessons)
      let area = n ? lessonPath + ' L' + sx(n - 1) + ',' + (H - PB) + ' L' + sx(0) + ',' + (H - PB) + ' Z' : ''
      return { W: W, H: H, PL: PL, PR: PR, PB: PB, grid: grid, lessons: lessons, loss: loss, lessonPath: lessonPath, lossPath: path(loss), area: area }
    },
    studentSub () {
      let np = this.students.filter(function (r) {
        return r.newPriority
      }).length
      return '近 ' + this.days + ' 天 · 新生优先 ' + np + ' 人置顶 · 其余按风险高 → 低'
    },
    studentInsight () {
      let rows = this.students
      if (!rows.length) {
        return ''
      }
      let hi = rows.filter(function (r) {
        return r.level === '高'
      }).length
      let np = rows.filter(function (r) {
        return r.newPriority
      }).length
      let inner = rows.reduce(function (s, r) {
        return s + num(r.innerCount)
      }, 0)
      return '近 ' + this.days + ' 天：' + rows.length + ' 名高频被请假学生中，' + hi + ' 人高风险，' + np +
        ' 人属于「新生优先」（开课 30 天内被请假 ≥ 2 次）。另有 ' + inner + ' 次请假发生在课前 3 小时内，不安排代课。'
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    num: num,
    isNum: isNum,
    fmtNum: fmtNum,
    fmtRate: fmtRate,
    fmtRateNum: fmtRateNum,
    shortDate: shortDate,
    levelBadge (level) {
      return LEVEL_BADGE[level] || 'is-gray'
    },
    load () {
      let self = this
      self.loading = true
      postImpact(self, '/leave', { days: self.days }).then(function (data) {
        if (data) {
          self.d = data
          self.loadedAt = self.$moment().format('YYYY-MM-DD HH:mm:ss')
        }
      }).finally(function () {
        self.loading = false
      })
    }
  }
}
</script>
<style scoped lang="less">
@import './parts/page.less';

.kpi-split {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-top: 16px;
}

.kpi-split-right {
  padding-left: 18px;
  border-left: 1px solid @line;
}

.kpi-split-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  color: @text;
  white-space: nowrap;
  small {
    margin-left: 3px;
    font-size: 13px;
    font-weight: 500;
    color: @text-muted;
  }
  &.is-warning {
    color: #C67511;
  }
}

.kpi-split-label {
  font-size: 12px;
  color: @text-muted;
}

.chart-legend {
  .is-proxy {
    background: #0B9E8D;
  }
  .is-loss {
    background: @danger;
  }
  .is-lessons {
    background: @warning;
  }
}

.bucket-axis {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: @text-muted;
  .is-danger {
    font-weight: 600;
    color: @danger;
  }
}

.mini-badge {
  &.is-inner {
    color: @danger;
    background: rgba(230, 70, 78, .1);
  }
  &.is-outer {
    color: @text-sub;
    background: rgba(201, 211, 232, .35);
  }
}
</style>



// WEBPACK FOOTER //
// TchImpactLeave.vue?9e3a6db4